import { reverse } from 'named-urls'
import React, { useCallback, useMemo } from 'react'
import { type Location, useLocation } from 'react-router-dom'

import { useDashboardFeatureFlags } from '@components'
import { ButtonClean } from '@fourthwall/components'
import { AnalyticsApiClient } from '@fourthwall/services/dist/AnalyticsApiClient'
import { useMutation } from '@tanstack/react-query'
import { routing } from '@utils/routing'

import { useShopTime } from '../../../../../Timezone/providers/ShopTimeProvider'
import { useAnalyticsParams } from '../../modules/AnalyticsParams'
import * as S from './AnalyticsReports.styled'
import type { Report, Type, Variables } from './AnalyticsReports.types'
import { AnalyticsReportsView } from './AnalyticsReportsView'
import { DownloadButton } from './components/DownloadButton/DownloadButton'
import { downloadCsv } from './utils/downloadCsv'

const reports: Record<Type, Omit<Report, 'type'>> = {
  AOV: {
    name: 'Average Order Value',
  },
  CUSTOMERS_OVER_TIME: {
    name: 'Customers Over Time',
  },
  SALES_OVER_TIME: {
    name: 'Sales Over Time',
  },
  SALES_OVER_TIME_DETAILED: {
    name: 'Sales Over Time (detailed)',
  },
  SALES_OVER_TIME_DONATION: {
    name: 'Sales Over Time (donations)',
  },
  SALES_OVER_TIME_MEMBERSHIP: {
    name: 'Sales Over Time (memberships)',
  },
  SALES_OVER_TIME_ORDER: {
    name: 'Sales Over Time (orders)',
  },
  FIRST_TIME_PURCHASES: {
    name: 'First Time Purchases',
    featureFlag: 'reportsFirstTimePurchases',
  },
  LTV: {
    name: 'Lifetime Value',
    featureFlag: 'reportsLTV',
  },
  SALES_BY_UTM: {
    name: 'Sales by UTM',
    featureFlag: 'reportsUTM',
  },
  SALES_BY_UTM_ORDER: {
    name: 'Sales by UTM (orders)',
    featureFlag: 'reportsUTM',
  },
  SALES_BY_UTM_MEMBERSHIP: {
    name: 'Sales by UTM (memberships)',
    featureFlag: 'reportsUTM',
  },
  SALES_BY_UTM_DONATION: {
    name: 'Sales by UTM (donations)',
    featureFlag: 'reportsUTM',
  },
}

export const createGetReportTo = (location: Location, slug: string) => {
  return {
    pathname: reverse(routing.analytics.overview.report.self, { reportType: slug }),
    search: location.search,
  }
}

export const AnalyticsReportsContainer = () => {
  const location = useLocation()
  const { timezone } = useShopTime()
  const { analyticsParams } = useAnalyticsParams()
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const { mutateAsync } = useMutation({
    mutationFn: async (variables: Variables) => {
      const { data } = await AnalyticsApiClient.getReportCsv(...variables)

      return data
    },
  })

  const getReportType = (report: Report) => {
    if (report.slug) {
      return <ButtonClean label={report.name} to={createGetReportTo(location, report.slug)} />
    }

    return report.name
  }

  const isEnabled = useCallback(
    (report: Report) => {
      return !report.featureFlag || dashboardFeatureFlags[report.featureFlag].enabled
    },
    [dashboardFeatureFlags],
  )

  const reportsData: Report[] = useMemo(() => {
    return (Object.entries(reports) as [Type, Omit<Report, 'type'>][])
      .map(([type, report]) => ({
        ...report,
        type,
      }))
      .filter(isEnabled)
  }, [isEnabled])

  const handleOnDownload = async (type: Type) => {
    const data = await mutateAsync([
      { type, otype: 'CSV' },
      { ...analyticsParams, zoneId: timezone },
    ])
    downloadCsv(data, `${type}-${analyticsParams.from}-${analyticsParams.to}.csv`)
  }

  const table = {
    data: reportsData,
    headers: [
      {
        id: 'name',
        label: 'Report Type',
        render: getReportType,
      },
      {
        id: 'download',
        label: 'Download CSV',
        render: (item: Report) => (
          <DownloadButton type="text" onDownload={() => handleOnDownload(item.type)} />
        ),
        alignment: 'right' as const,
      },
    ],
  }

  const list = {
    data: reportsData,
    renderItem: (item: Report) => (
      <S.ListItem>
        <div>{getReportType(item)} </div>
        <DownloadButton type="icon" onDownload={() => handleOnDownload(item.type)} />
      </S.ListItem>
    ),
  }

  return <AnalyticsReportsView table={table} list={list} />
}
